interface NetworkInformation {
    type?: string
    effectiveType?: string
    downlink?: number
    rtt?: number
    saveData?: boolean
}

interface NavigatorUAData {
    platform?: string
}

/**
 * Collects client-side device context information useful for chatbot interactions.
 * No additional permissions are required — all values are available by default in the browser.
 */
export function collectDeviceContext() {
    const connection = (
        navigator as Navigator & { connection?: NetworkInformation }
    ).connection

    const uaData = (
        navigator as Navigator & { userAgentData?: NavigatorUAData }
    ).userAgentData

    return {
        dateTime: new Date().toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
        languages: [...navigator.languages],
        url: window.location.href,
        pathname: window.location.pathname,
        pageTitle: document.title,
        referrer: document.referrer || undefined,
        screenWidth: screen.width,
        screenHeight: screen.height,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        devicePixelRatio: window.devicePixelRatio,
        maxTouchPoints: navigator.maxTouchPoints,
        hardwareConcurrency: navigator.hardwareConcurrency,
        deviceMemory: (navigator as Navigator & { deviceMemory?: number })
            .deviceMemory,
        platform: uaData?.platform,
        connectionType: connection?.type,
        connectionEffectiveType: connection?.effectiveType,
        connectionDownlink: connection?.downlink,
        connectionRtt: connection?.rtt,
        saveData: connection?.saveData,
        darkMode: window.matchMedia('(prefers-color-scheme: dark)').matches,
        reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)')
            .matches,
        historyLength: window.history.length,
    }
}

export type GeolocationData = {
    geolocationLatitude: number
    geolocationLongitude: number
    geolocationAccuracy?: number
    geolocationCity?: string
}

export type ReverseGeocodeFn = (
    lat: number,
    lon: number,
) => Promise<string | undefined>

/**
 * Collects geolocation data if the user has already granted permission.
 * Uses the Permissions API to avoid triggering a browser prompt.
 * Returns null if permission is not granted or geolocation is unavailable.
 * @param reverseGeocode - optional callback to resolve city name from coordinates (e.g. via backend proxy)
 */
export async function collectGeolocation(
    reverseGeocode?: ReverseGeocodeFn,
): Promise<GeolocationData | null> {
    if (!navigator.geolocation || !navigator.permissions) {
        return null
    }

    try {
        const permission = await navigator.permissions.query({
            name: 'geolocation',
        })
        if (permission.state !== 'granted') {
            return null
        }

        const position = await new Promise<GeolocationPosition>(
            (resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                    timeout: 10_000,
                })
            },
        )

        const { latitude, longitude, accuracy } = position.coords
        const city = reverseGeocode
            ? await reverseGeocode(latitude, longitude).catch(() => undefined)
            : undefined

        return {
            geolocationLatitude: latitude,
            geolocationLongitude: longitude,
            geolocationAccuracy: accuracy,
            geolocationCity: city,
        }
    } catch {
        return null
    }
}
